import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { SessionService } from 'src/app/auth/session/session.service';
import {
  ActionModalComponent,
  ActionModalResult,
} from 'src/app/shared/action-modal/action-modal.component';
import { Module } from '../../modules/services/firmware/firmware.service';
import { CreateReleaseComponent } from '../create-release/create-release.component';
import { ReleaseApprovalModalComponent } from '../release-approval-modal/release-approval-modal.component';
import { ReleaseViewConfigModalComponent } from '../release-view-config-modal/release-view-config-modal.component';
import {
  Release,
  ReleaseService,
  StatusChangeRequest,
} from '../services/release.service';

@Component({
  selector: 'dcc-release-view-modal',
  templateUrl: './release-view-modal.component.html',
  styleUrls: ['./release-view-modal.component.scss'],
})
export class ReleaseViewModalComponent implements OnInit {
  @Output() refreshPage: EventEmitter<any> = new EventEmitter();
  release: Release;
  releaseId: string;
  loading = true;
  releaseQuery: string;

  constructor(
    private activeModal: NgbActiveModal,
    private releaseService: ReleaseService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private sessionService: SessionService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  async ngOnInit() {
    await this.getReleaseData();
    this.setUrlParam(this.releaseId);
  }

  async getReleaseData() {
    this.loading = true;
    this.release = await this.releaseService.getRelease(this.releaseId);
    if (
      this.release.status === 'Rejected' ||
      this.release.status === 'Disabled'
    ) {
      this.release.phases.map(
        (phase, index) =>
          (this.release.phases[index].status = this.release.status),
      );
    }
    this.loading = false;
  }

  async editRelease() {
    const modal = this.modalService.open(CreateReleaseComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });
    modal.componentInstance.isUpdate = true;
    modal.componentInstance.releaseToUpdate = this.release;
    modal.componentInstance.closePage.subscribe(
      async () => await this.getReleaseData(),
    );
  }

  confirmDisableRelease() {
    const modal = this.modalService.open(ActionModalComponent, {
      windowClass: 'lytxModal',
      centered: true,
    });

    modal.componentInstance.config = {
      title: 'Disable ' + this.release.name,
      body:
        'Are you sure sure you want to disable this release? This will automatically pause deployment of modules and ' +
        'prevent modules from being deployed to additional cohorts. Cohorts that have already been updated with the ' +
        'module will not be reverted to the previous version.',
      action: 'Disable',
    };

    modal.closed.subscribe(async (result: ActionModalResult) => {
      if (result.success) {
        await this.disableRelease();
      }
    });
  }

  async disableRelease() {
    try {
      const statusChangeRequest: StatusChangeRequest = {
        status: 'Disabled',
        requestCreator: this.getUserName(),
      };
      await this.releaseService.changeReleaseStatus(
        this.releaseId,
        statusChangeRequest,
      );
      await this.getReleaseData();
    } catch {
      this.toastr.error('Error disabling release');
    }
  }

  confirmEnableRelease() {
    const modal = this.modalService.open(ActionModalComponent, {
      windowClass: 'lytxModal',
      centered: true,
    });

    modal.componentInstance.config = {
      title: 'Enable ' + this.release.name,
      body: 'Are you sure sure you want to enable this release? This will automatically resume deployment of modules to cohorts, and re-enable the ability to deploy to additional phases.',
      action: 'Enable',
    };

    modal.closed.subscribe(async (result: ActionModalResult) => {
      if (result.success) {
        await this.enableRelease();
      }
    });
  }

  async enableRelease() {
    try {
      const statusChangeRequest: StatusChangeRequest = {
        status: 'Pending',
        requestCreator: this.getUserName(),
      };
      await this.releaseService.changeReleaseStatus(
        this.releaseId,
        statusChangeRequest,
      );
      await this.getReleaseData();
    } catch {
      this.toastr.error('Error enabling release');
      this.close();
    }
  }

  async onDeleteClick() {
    const modal = this.modalService.open(ActionModalComponent, {
      windowClass: 'deleteReleaseModal',
      centered: true,
    });

    modal.componentInstance.config = {
      title: 'Delete Release ' + this.release.name,
      body:
        'Are you sure you want to delete this release? This will automatically pause deployment of ' +
        'modules and prevent modules from being deployed to additional cohorts. ' +
        'Cohorts that have already been updated with the module will not be reverted to the previous version.',
      action: 'Delete',
    };

    modal.closed.subscribe(async (result: ActionModalResult) => {
      if (result.success) {
        await this.deleteRelease();
      }
    });
  }

  async deleteRelease() {
    try {
      await this.releaseService.deleteRelease(
        this.release.id,
        this.getUserName(),
      );
      this.toastr.success('Release Deleted');
      this.close();
    } catch (e) {
      this.toastr.error('Error Deleting a Release');
      this.close();
    }
  }

  confirmPhaseDeployment(phaseName: string) {
    const modal = this.modalService.open(ActionModalComponent, {
      windowClass: 'lytxModal',
      centered: true,
    });

    modal.componentInstance.config = {
      title: `Deploy ${phaseName}`,
      body: 'Are you sure sure you want to deploy this release? This will deploy selected modules to all devices in the cohort.',
      action: 'Deploy',
    };

    modal.closed.subscribe((result: ActionModalResult) => {
      if (result.success) {
        this.deployPhase(phaseName);
      }
    });
  }

  async deployPhase(phaseName: string) {
    try {
      await this.releaseService.deployPhase(
        phaseName,
        this.release.id,
        this.getUserName(),
      );
      await this.getReleaseData();
    } catch (e) {
      this.toastr.error('Error Deploying Phase: ' + e.error.message);
    }
  }

  getStatusCss(status: string): string {
    switch (status) {
      case 'PendingReview':
        return 'pending';
      case 'InProgress':
        return 'in_progress';
      default:
        return status.toLowerCase();
    }
  }

  getStatusName(status: string): string {
    let baseStatus = status;
    switch (status) {
      case 'PendingReview':
        return 'Pending Review';
      case 'InProgress':
        baseStatus = 'In Progress';
        break;
      default:
        break;
    }

    if (this.release.hasPendingApproval) {
      return `${baseStatus} (Awaiting Approval)`;
    }

    return baseStatus;
  }

  disableDeployPhaseBtn(expectedReleaseDate: string): boolean {
    return (
      new Date(expectedReleaseDate) > new Date() ||
      this.release.status === 'Rejected' ||
      this.release.status === 'Disabled'
    );
  }

  async close() {
    this.refreshPage.emit();
    this.activeModal.close(false);
    await this.setUrlParam();
  }

  displayConfig(module: Module) {
    const modal = this.modalService.open(ReleaseViewConfigModalComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });
    modal.componentInstance.headerText = `JSON Configuration`;
    modal.componentInstance.bodyText = module.configuration;
  }

  isConfigAvailable(module: Module) {
    if (module) {
      if (module.configuration) {
        return true;
      }
    }
    return false;
  }

  onReview() {
    const modal = this.modalService.open(ReleaseApprovalModalComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });
    modal.componentInstance.refreshPage.subscribe(
      async () => await this.close(),
    );
    modal.componentInstance.release = this.release;
  }

  private getUserName(): string {
    return this.sessionService.getCurrentSession().name;
  }

  private setUrlParam(releaseId?: string): Promise<boolean> {
    if (releaseId) {
      return this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          query: this.releaseQuery,
          releaseId: releaseId ?? '',
        },
      });
    }
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: this.releaseQuery,
      },
    });
  }
}
